import useApiRequest from "@/lib/hooks/use-request";
import { DashboardResponseDto } from "@/lib/interfaces/dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui-components/ui/card";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import BarChart from "@/ui-components/ui/visualization/bar-chart";
import PieChart from "@/ui-components/ui/visualization/pie-chart";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { useCallback, useEffect, useState } from "react";

const DashboardOld = () => {
  const { apiRequest } = useApiRequest();

  const [item, setItem] = useState<DashboardResponseDto>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    let url = "dashboard";
    setIsLoading(true);
    const response = await apiRequest<DashboardResponseDto>(url, "GET");
    if (response.data) {
      setItem(response.data);
    }
    setIsLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(data: DashboardResponseDto) => <DashboardContent item={data} />}
    />
  );
};

const DashboardContent = ({ item }: { item: DashboardResponseDto }) => {
  if (!item.downlineStatistics && !item.ownStatistics) {
    return (
      <div>
        <PageTitleContainer>
          <div className="flex flex-row justify-between items-center">
            <PageTitle>Dashboard</PageTitle>
          </div>
        </PageTitleContainer>
        <div className="p-4 items-center m-auto">
          <Card>
            <CardContent className="flex items-center justify-center min-h-[200px]">
              <p className="text-muted-foreground">No statistics data available</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>Dashboard</PageTitle>
        </div>
      </PageTitleContainer>
      <div className="p-4 flex flex-col gap-8">
        {item.ownStatistics && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Persönliche Statistiken</h2>
            <div className="flex flex-row gap-4">
              <div className="grow flex flex-col gap-4">
                <PieChart
                  title="Gewonnen / Verloren Verhältnis"
                  description="Hier findest du das Verhältnis von Gewonnen und Verlorenen Verkäufen"
                  data={item.ownStatistics.winLoseRatio}
                />
                <BarChart
                  title="Verkäufe pro Monat"
                  description="Hier findest du die Verkäufe pro Monat"
                  data={item.ownStatistics.salesByMonth}
                />
              </div>
              <div className="grow w-1/2 flex flex-col gap-4">
                <PieChart
                  title="Stornierungsverhältnis"
                  description="Hier findest du das Verhältnis von Stornierten und nicht Stornierten Verkäufen"
                  data={item.ownStatistics.cancellationRatio}
                />
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Stats</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Offene Deals</span>
                        <span className="font-medium">{item.ownStatistics.openDeals}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Sales bis zum nächsten Rang</span>
                        <span className="font-medium">{item.ownStatistics.salesUntilNextRank}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {item.downlineStatistics && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Downline Statistiken</h2>
            <div className="flex flex-row gap-4">
              <div className="grow w-1/2 flex flex-col gap-4">
                {item.downlineStatistics.winLoseRatios.map((ratio) => (
                  <PieChart
                    title={ratio.label}
                    description={`${ratio.label} Gewonnen / Verloren Verhältnis`}
                    data={ratio}
                    key={ratio.label}
                  />
                ))}
              </div>
              <div className="grow w-1/2 flex flex-col gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Verkäufe bis zum nächsten Rang</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4">
                    {item.downlineStatistics.salesUntilNextRank.map((stat, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-muted-foreground">{stat.label}</span>
                        <span className="font-medium">{stat.value}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <BarChart
                  title="Verkäufe pro Monat"
                  description="Hier findest du die Verkäufe pro Monat"
                  data={item.downlineStatistics.salesByMonth}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardOld;
